import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, Upload, Download, Trash2, Loader2 } from "lucide-react";

const CAT_LABEL = {
  scope_of_appointment: "Scope of Appointment", enrollment: "Enrollment", id_card: "ID Card",
  formulary: "Formulary", correspondence: "Correspondence", other: "Other",
};

export default function HouseholdDocuments({ hid }) {
  const [docs, setDocs] = useState(null);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("other");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef();

  const load = async () => {
    const { data } = await api.get(`/agent/households/${hid}/documents`);
    setDocs(data.documents); setCats(data.categories || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hid]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", f); fd.append("category", cat);
      await api.post(`/agent/households/${hid}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (e2) { setErr(e2?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const view = async (d) => {
    const res = await api.get(`/agent/documents/${d.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  const remove = async (d) => { await api.delete(`/agent/documents/${d.id}`); await load(); };

  return (
    <div className="space-y-4" data-testid="household-documents">
      <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-4">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1">
            <label className="text-xs font-semibold uppercase tracking-wide text-[#6B7280]">Category</label>
            <select value={cat} onChange={(e) => setCat(e.target.value)} className="mt-1 h-10 w-full rounded-md border bg-white px-2 text-sm" data-testid="doc-category">
              {(cats.length ? cats : ["other"]).map((c) => <option key={c} value={c}>{CAT_LABEL[c] || c}</option>)}
            </select>
          </div>
          <input ref={fileRef} type="file" onChange={upload} className="hidden" data-testid="doc-file-input"
            accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.csv,.txt" />
          <Button onClick={() => fileRef.current?.click()} disabled={busy} className="h-10 bg-[#0B192C] text-white" data-testid="doc-upload-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Upload document
          </Button>
        </div>
        <p className="text-xs text-[#9CA3AF] mt-2">Files are stored securely and visible only to staff assigned to this household. Max 20MB.</p>
        {err && <p className="text-rose-600 text-sm mt-2" data-testid="doc-error">{err}</p>}
      </div>

      {!docs ? <Skeleton className="h-40 w-full rounded-xl" /> : docs.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-[#E5E5E0] bg-white p-8 text-center text-[#6B7280]" data-testid="documents-empty">No documents on record yet. Upload one above.</div>
      ) : (
        <div className="rounded-xl border-2 border-[#E5E5E0] bg-white divide-y" data-testid="documents-list">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-3 p-3" data-testid={`document-${d.id}`}>
              <FileText className="w-5 h-5 text-[#0B192C] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#0B192C] truncate">{d.original_filename}</p>
                <p className="text-xs text-[#9CA3AF]">
                  {CAT_LABEL[d.category] || d.category} · {d.source === "agent_upload" ? "Agent upload" : d.source}
                  {d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ""} · {(d.created_at || "").slice(0, 10)}
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => view(d)} data-testid={`document-view-${d.id}`}><Download className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => remove(d)} data-testid={`document-delete-${d.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
